import { useContext, useState, useEffect, useMemo } from 'react'
import ErrorOverlay from 'components/ErrorOverlay'
import styles from './SearchResultsCarousel.pcss'
import { FormattedMessage } from 'react-intl'
import { NavLink } from 'react-router'
import CardList from 'components/CardList'
import { AnyCard, LoadingCard } from 'components/Card'
import {
  SEARCH_FILTER_KEY_GIFS,
  SEARCH_FILTER_KEY_POST_IDEAS,
  SEARCH_FILTER_KEY_QUOTES,
  SEARCH_FILTER_KEY_STOCK,
  SEARCH_FILTER_KEY_THREADS,
  SearchFilterKey
} from 'routes/search/state/types'
import StatusIdeaCard from 'components/Card/StatusIdeaCard'
import { SearchContext } from 'routes/search/state/context'
import { searchRequest, updateState } from 'routes/search/state/actions'
import ContentTypeFilter from 'components/ContentTypeFilter'
import RangeFilterSelect from 'components/RangeFilterSelect'
import { RangeFilter } from 'interfaces'
import { ARTICLE_TYPE, PHOTO_TYPE, STOCK_PHOTO_TYPE, STOCK_VIDEO_TYPE, TEXT_TYPE, VIDEO_TYPE } from 'shared/constants'
import HorizontalList from 'components/HorizontalList'
import { QueryChip } from 'components/ContentPanels/components/QueryChip'

const CAROUSEL_PAGE_SIZE_TEXTS = 40

export function SearchContentCarousel({ filter }: { filter: SearchFilterKey }) {
  const [{
    results, query, quotesTags, selectedFilters, postIdeasTags, ideasTag, quotesTag, threadsType, threadsRange
  }, searchDispatch] = useContext(SearchContext)
  const { loading, items, error, hasNextPage, page } = results[filter]
  const [activeStockType, setActiveStockType] = useState(STOCK_PHOTO_TYPE)
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [textsPage, setTextsPage] = useState(1) // Quotes and Status ideas pagination
  const withQuery = !loading && query.length
  const isEmpty = items.length === 0 && !withQuery
  const isTextFilter = filter === SEARCH_FILTER_KEY_QUOTES || filter === SEARCH_FILTER_KEY_POST_IDEAS
  // NOTE: There's no server-side pagination for quotes and status ideas searches by tag only
  const noTextsPaging = isTextFilter && page === 0 && items.length > CAROUSEL_PAGE_SIZE_TEXTS

  const loadNextPage = () => {
    if (noTextsPaging) {
      if (textsPage * CAROUSEL_PAGE_SIZE_TEXTS < items.length) {
        setTextsPage(textsPage + 1)
      }
      return
    }
    if (hasNextPage && items.length !== 0 && !loading) {
      searchDispatch(searchRequest(query, [filter], page + 1))
    }
  }

  const onTagClick = (value: string, _isTag?: boolean, isQuote?: boolean) => {
    const shouldClearTag = activeTag === value
    if (isQuote) {
      const qTag = { value: shouldClearTag ? '' : value, clear: shouldClearTag }
      const siTag = { value: ideasTag || '', clear: Boolean(ideasTag) }
      searchDispatch(searchRequest(query, selectedFilters, 0, qTag, siTag))
    } else {
      const qTag = { value: quotesTag || '', clear: Boolean(quotesTag) }
      const siTag = { value: shouldClearTag ? '' : value, clear: shouldClearTag }
      searchDispatch(searchRequest(query, selectedFilters, 0, qTag, siTag))
    }
    setActiveTag(shouldClearTag ? null : value)
  }

  const onThreadsTypeChange = (type: string) => {
    searchDispatch(updateState({ threadsType: type }))
  }

  const onThreadsRangeChange = (range: RangeFilter) => {
    searchDispatch(updateState({ threadsRange: range }))
  }

  useEffect(() => {
    if (query) {
      setActiveTag(null)
    }
  }, [query])

  const cards = useMemo(() => {
    const defaultCardsCount = 20
    const isGifFilter = filter === SEARCH_FILTER_KEY_GIFS
    const loadingCards: any[] = []
    const emptyCards: any[] = []
    if (loading) {
      for (let i = 0; i < defaultCardsCount; i++) {
        loadingCards.push(<LoadingCard active key={`loading-${i}`} small square mediaSpinner={isGifFilter} />)
      }
    }
    if (!loading && items.length === 0) {
      for (let i = 0; i < defaultCardsCount; i++) {
        emptyCards.push(<LoadingCard square active={false} small key={`empty-${i}`} />)
      }
    }

    const filteredItems = filter === SEARCH_FILTER_KEY_STOCK
      ? items.filter(item => item.type === activeStockType)
      : items

    const contentCards = isTextFilter
      ? items.slice(0, textsPage * CAROUSEL_PAGE_SIZE_TEXTS).map((quote: any) => <StatusIdeaCard key={quote.id} small content={quote} />)
      : filteredItems.map((item) => <AnyCard content={item} small key={item.id} square children={item} />)

    return contentCards.concat(loadingCards).concat(emptyCards)
  }, [loading, items, filter, activeStockType, isTextFilter, textsPage])

  const tags = filter === SEARCH_FILTER_KEY_QUOTES
    ? quotesTags
    : filter === SEARCH_FILTER_KEY_POST_IDEAS ? postIdeasTags : []

  return (
    <section
      className={`${styles.container} ${isEmpty && tags.length === 0 ? styles.empty : ''}`}
      data-testid="search-result-section"
    >
      <header className={styles.header}>
        <h2 className="text-ellipsis">
          <FormattedMessage id={`search.navigation.${filter}`} />
        </h2>
        {items.length !== 0 && (
          <NavLink
            to={{ pathname: `/content/search/content/${filter}`, search: query }}
            state={{ stockFilter: activeStockType }}
          >
            <FormattedMessage id="general.carousel.nav.see-all" />
          </NavLink>
        )}
        {filter === SEARCH_FILTER_KEY_STOCK && (
          <div className={styles.filters}>
            <ContentTypeFilter
              options={[STOCK_PHOTO_TYPE, STOCK_VIDEO_TYPE]}
              selected={activeStockType}
              onFilterSelected={setActiveStockType}
            />
          </div>
        )}
        {filter === SEARCH_FILTER_KEY_THREADS && (
          <div className={styles.filters}>
            <ContentTypeFilter
              options={[TEXT_TYPE, ARTICLE_TYPE, VIDEO_TYPE, PHOTO_TYPE]}
              selected={threadsType}
              onFilterSelected={onThreadsTypeChange}
            />
            <RangeFilterSelect
              range={threadsRange}
              onRangeSelected={onThreadsRangeChange}
            />
          </div>
        )}
        {tags.length > 0 && (
          <HorizontalList>
            {tags.map(tag => (
              <QueryChip
                isQuote={filter === SEARCH_FILTER_KEY_QUOTES}
                key={tag}
                label={tag}
                active={tag === activeTag}
                onClick={onTagClick}
              />
            ))}
          </HorizontalList>
        )}
      </header>
      <CardList
        key={`${query}${activeTag}`}
        containerClassName={styles.carousel}
        disabled={items.length === 0}
        onScrollLimit={loadNextPage}
      >
        {cards}
      </CardList>
      {error && (
        <ErrorOverlay
          error={error}
          subtitle=""
          originalError={error}
          className={`${styles['error-box']} ${isTextFilter ? styles['error-box-text'] : ''}`}
          small
        />
      )}
    </section>
  )
}

export default SearchContentCarousel
